/**
 * Trigger a browser download for an in-memory blob.
 *
 * On iOS Safari (and iPadOS Safari running in desktop-mode) the
 * `<a download>` attribute is silently ignored — clicking the link
 * navigates to the blob URL and shows an inline preview instead of
 * downloading the file. We detect that case and use the Web Share
 * API, which surfaces the native iOS share sheet with options like
 * "Save to Files" and "Save Image". On every other platform the
 * anchor approach works and we use it.
 */
export const downloadBlob = async (blob: Blob, filename: string): Promise<void> => {
  if (isIosLike()) {
    const ok = await tryWebShare(blob, filename);
    if (ok) return;
    // Web Share unavailable or it failed in a way we don't recognise.
    // Fall through to the anchor — on iOS that opens an inline
    // preview the user can long-press to save, which is at least
    // recoverable.
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.append(a);
  a.click();
  a.remove();
  // Defer revoke. iOS Safari needs the URL to stay valid long enough
  // for the download to actually attach to the navigation; revoking on
  // the next tick is too aggressive for multi-MB blobs and can produce
  // an empty/cancelled download on mobile.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60_000);
};

export const downloadText = (text: string, filename: string, mime = 'text/plain'): Promise<void> =>
  downloadBlob(new Blob([text], { type: mime }), filename);

/** True for iPhone, iPad, and iPod across Safari/WebKit, including
 *  iPadOS 13+ which sends a desktop UA but is still subject to the
 *  same `<a download>` limitation. */
const isIosLike = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  // iPadOS in desktop-mode: spoofs a Mac UA, but is the only "Mac"
  // that reports more than one touch point.
  if (ua.includes('Macintosh') && navigator.maxTouchPoints > 1) return true;
  return false;
};

/** Try the Web Share API. Returns true if the share sheet was shown
 *  (or if the user explicitly cancelled it, which is also a "we
 *  handled this, don't fall through" outcome). False if the API is
 *  unavailable or threw something we don't recognise. */
const tryWebShare = async (blob: Blob, filename: string): Promise<boolean> => {
  if (typeof navigator === 'undefined') return false;
  type NavShare = Navigator & {
    share?: (data: { files?: File[]; title?: string; text?: string }) => Promise<void>;
    canShare?: (data: { files?: File[] }) => boolean;
  };
  const nav = navigator as NavShare;
  if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') return false;

  const file = new File([blob], filename, {
    type: blob.type || 'application/octet-stream',
  });
  if (!nav.canShare({ files: [file] })) return false;

  try {
    await nav.share({ files: [file] });
    return true;
  } catch (err) {
    // User dismissed the sheet — they made a deliberate choice, don't
    // surprise them by also opening the inline-preview fallback.
    if (err instanceof Error && err.name === 'AbortError') return true;
    return false;
  }
};
